import { IHttpClient } from '../../application/interfaces/http-client.interface';
import { InvalidCredentialsError } from '../../application/errors/invalid-credentials.error';
import { NetworkError } from '../../application/errors/network.error';

interface BackendErrorResponse {
  success: false;
  error?: {
    code?: string;
    message?: string;
    details?: Array<{ field: string; message: string }>;
  };
}

export class FetchHttpClient implements IHttpClient {
  private readonly inFlightRequests = new Map<string, Promise<unknown>>();

  constructor(
    private readonly baseUrl: string,
    private readonly tokenProvider?: () => string | null
  ) {}

  public async get<T>(url: string, headers?: Record<string, string>): Promise<T> {
    const cacheKey = `${url}_${JSON.stringify(headers || {})}`;
    const existing = this.inFlightRequests.get(cacheKey);
    if (existing) {
      return existing as Promise<T>;
    }

    const promise = this.request<T>(url, {
      method: 'GET',
      headers,
    }).finally(() => {
      this.inFlightRequests.delete(cacheKey);
    });

    this.inFlightRequests.set(cacheKey, promise);
    return promise;
  }

  public async post<T>(url: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(url, {
      method: 'POST',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  public async put<T>(url: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(url, {
      method: 'PUT',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  public async patch<T>(url: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(url, {
      method: 'PATCH',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  public async delete<T>(url: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(url, {
      method: 'DELETE',
      headers,
    });
  }

  private async request<T>(endpoint: string, options: RequestInit): Promise<T> {
    const fullUrl = endpoint.startsWith('http')
      ? endpoint
      : `${this.baseUrl.replace(/\/$/, '')}/${endpoint.replace(/^\//, '')}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    const token = this.tokenProvider ? this.tokenProvider() : null;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response: Response;
    try {
      response = await fetch(fullUrl, {
        ...options,
        headers,
      });
    } catch (networkErr) {
      throw new NetworkError(
        networkErr instanceof Error
          ? `Error de red: ${networkErr.message}`
          : 'No se pudo conectar con el servidor backend.'
      );
    }

    let data: unknown;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        data = await response.json();
      } catch {
        data = null;
      }
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      this.handleHttpError(response.status, data);
    }

    return data as T;
  }

  private handleHttpError(status: number, errorData: unknown): never {
    const errObj = errorData as BackendErrorResponse | null;

    if (status === 401 || errObj?.error?.code === 'INVALID_CREDENTIALS') {
      if (typeof window !== 'undefined') {
        try {
          localStorage.removeItem('bartech_access_token');
          localStorage.removeItem('bartech_user');
        } catch {
          // Ignore storage errors
        }
        window.dispatchEvent(
          new CustomEvent('auth:unauthorized', {
            detail: {
              code: errObj?.error?.code || 'UNAUTHORIZED',
              message: errObj?.error?.message || 'Tu sesión ha expirado o es inválida.',
            },
          })
        );
      }

      throw new InvalidCredentialsError(
        errObj?.error?.message || 'Correo electrónico o contraseña incorrectos.'
      );
    }

    if (status === 400 && errObj?.error?.code === 'VALIDATION_ERROR') {
      const detailsMsg = errObj.error.details
        ?.map((d) => d.message)
        .join(', ');
      throw new Error(detailsMsg || errObj.error.message || 'Datos de formulario inválidos.');
    }

    if (errObj?.error?.message) {
      throw new Error(errObj.error.message);
    }

    throw new Error(`Error en la petición al servidor (Código HTTP ${status}).`);
  }
}
